import React, { useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { useWindowResizeEvent } from '@wix/da-hooks/pkg/useWindowResizeEvent';
import { DeviationViewContext } from '../Context';
import DeviationPageContent from './_partials/DeviationPageContent';
import SiteFooter from '../SiteFooter';
import { DeviationViewMode } from '@wix/da-react-context/pkg/publicSession';
import { useFullscreenEvents } from '../utils/hooks/useFullscreenEvents';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useDuperbrowseViewType } from '../Duperbrowse/DuperbrowseViewContext';
import { SubscriptionPurchaseFTUEContextProvider } from '../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import {
  getBiEventComponentByType,
  getPageTitle,
  getDeviationAppUrl,
} from './redux/helpers';
import {
  getDeviationType,
  getDeviationResourceType,
} from '../DeviationViews/_helpers';
import Notifications from '../Notifications';
import { useDeviationChangedCallback } from './hooks/useDeviationChangedCallback';
import { useBoostModalOnLoad } from './hooks/useBoostModalOnLoad';
import DeviationPageHeader from './_partials/DeviationPageHeader';
import DeviationPageLoader from './_partials/DeviationPageLoader';
import { MatureDeviationContextProvider } from '../Context/MatureDeviationContext';
import { useMetaRobots } from '@wix/da-react-app/pkg/client/Head/hooks/useMetaRobots';
import DevpageAdhesionPlacement from '../DealerAd/Placement/DevpageAdhesion';

import s from './DeviationPage.scss';

export interface Props {
  deviation?: PapiDeviation;
  viewMode: DeviationViewMode;
  onLoad: (deviation: PapiDeviation) => void;
  onFullscreenEntered: (deviation: PapiDeviation) => void;
  onFullscreenExited: (deviation: PapiDeviation) => void;
  onScreenResize: (width: number, height: number) => void;
}

const DeviationPage: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  onLoad,
  viewMode,
  onFullscreenEntered,
  onFullscreenExited,
  onScreenResize,
}) => {
  const { t } = useTranslation();
  const { isInDuperbrowse } = useDuperbrowseViewType();
  useFullscreenEvents({
    enabled: true,
    onFullscreenEntered: () => deviation && onFullscreenEntered(deviation),
    onFullscreenExited: () => deviation && onFullscreenExited(deviation),
  });
  useWindowResizeEvent(onScreenResize);
  useDeviationChangedCallback(deviation, onLoad);
  const boostModal = useBoostModalOnLoad(deviation);
  const robots = useMetaRobots('', deviation);

  useEffect(() => {
    onScreenResize(window.innerWidth, window.innerHeight);
  }, [onScreenResize]);

  const biComponent = getBiEventComponentByType(getDeviationType(deviation));
  const isFullscreen = viewMode === DeviationViewMode.Fullscreen;

  const showHeaderAndFooter = !isFullscreen;
  const appUrl = getDeviationAppUrl(deviation);

  return (
    <DeviationViewContext.Provider value={viewMode}>
      {isInDuperbrowse && <title>{String(getPageTitle(deviation, t))}</title>}
      {isInDuperbrowse && appUrl && (
        <meta property="da:appurl" content={`DeviantArt://${appUrl}`} />
      )}
      {isInDuperbrowse && robots && <meta name="robots" content={robots} />}
      <main
        role="main"
        className={classnames(s['root'], isFullscreen && s['fullscreen'])}
      >
        {showHeaderAndFooter && <DeviationPageHeader />}

        {!deviation && <DeviationPageLoader />}
        {deviation && (
          <BiLoggerContextProvider
            value={{
              view: 'submission',
              typeid: getDeviationResourceType(deviation),
              itemid: deviation.deviationId,
              page_typeid: getDeviationResourceType(deviation),
              page_itemid: deviation.deviationId,
              creatorid: deviation.author?.useridUuid ?? '',
              component: biComponent,
              sectionname: 'deviation',
            }}
          >
            <MatureDeviationContextProvider deviation={deviation}>
              <SubscriptionPurchaseFTUEContextProvider deviation={deviation}>
                <DeviationPageContent deviation={deviation} />
              </SubscriptionPurchaseFTUEContextProvider>
            </MatureDeviationContextProvider>
          </BiLoggerContextProvider>
        )}

        {showHeaderAndFooter && <SiteFooter />}
      </main>
      <Notifications
        wrapperClassName={classnames(
          viewMode === DeviationViewMode.Normal && s['stage-notifications']
        )}
      />
      {boostModal}
      <DevpageAdhesionPlacement />
    </DeviationViewContext.Provider>
  );
};
DeviationPage.displayName = 'DeviationPage';

export default DeviationPage;
